import { computed, ref, unref, watch } from 'vue';
import RealEstateBrokerCommissionsAPI from 'dashboard/api/realEstateBrokerCommissions';
import RealEstateBrokerPaymentMethodsAPI from 'dashboard/api/realEstateBrokerPaymentMethods';

const requestErrorMessage = (requestError, fallback) =>
  requestError?.response?.data?.errors?.[0] ||
  requestError?.response?.data?.message ||
  requestError?.response?.data?.error ||
  fallback;

export function useRealEstateBrokerPaymentMethods({
  installmentId,
  notify,
  t,
} = {}) {
  const methods = ref([]);
  const catalog = ref({ banks: [], pix_keys: [] });
  const selectedId = ref(null);
  const loading = ref(false);
  const saving = ref(false);
  const error = ref('');

  const load = async () => {
    loading.value = true;
    error.value = '';
    try {
      const [methodsResponse, catalogResponse] = await Promise.all([
        RealEstateBrokerPaymentMethodsAPI.list(),
        RealEstateBrokerPaymentMethodsAPI.catalog(),
      ]);
      methods.value = methodsResponse.data.data || [];
      catalog.value = catalogResponse.data.data || { banks: [], pix_keys: [] };
      selectedId.value =
        methods.value.find(item => item.principal)?.id ||
        methods.value[0]?.id ||
        null;
    } catch (loadError) {
      error.value = requestErrorMessage(
        loadError,
        t('REAL_ESTATE_BROKER_COMMISSION.ERRORS.PAYMENT_METHODS')
      );
      if (notify) notify(error.value, null, { variant: 'error' });
    } finally {
      loading.value = false;
    }
  };

  const save = async (payload, paymentMethodId = null) => {
    saving.value = true;
    error.value = '';
    try {
      if (paymentMethodId) {
        await RealEstateBrokerPaymentMethodsAPI.update(
          paymentMethodId,
          payload
        );
      } else {
        await RealEstateBrokerPaymentMethodsAPI.create(payload);
      }
      await load();
      if (notify) {
        const successMessage = paymentMethodId
          ? t('REAL_ESTATE_BROKER_COMMISSION.ALERTS.METHOD_UPDATED')
          : t('REAL_ESTATE_BROKER_COMMISSION.ALERTS.METHOD_SAVED');
        notify(successMessage);
      }
      return true;
    } catch (saveError) {
      error.value = requestErrorMessage(
        saveError,
        t('REAL_ESTATE_BROKER_COMMISSION.ERRORS.METHOD_SAVE')
      );
      if (notify) notify(error.value, null, { variant: 'error' });
      return false;
    } finally {
      saving.value = false;
    }
  };

  const select = async ({ notifySuccess = true } = {}) => {
    const id = unref(installmentId);
    if (!id || !selectedId.value) return false;
    saving.value = true;
    error.value = '';
    try {
      await RealEstateBrokerCommissionsAPI.selectPaymentMethod(
        id,
        selectedId.value
      );
      if (notify && notifySuccess)
        notify(t('REAL_ESTATE_BROKER_COMMISSION.ALERTS.METHOD_SELECTED'));
      return true;
    } catch (selectError) {
      error.value = requestErrorMessage(
        selectError,
        t('REAL_ESTATE_BROKER_COMMISSION.ERRORS.METHOD_SELECT')
      );
      if (notify) notify(error.value, null, { variant: 'error' });
      return false;
    } finally {
      saving.value = false;
    }
  };

  watch(() => unref(installmentId), load, { immediate: true });

  return {
    methods,
    catalog,
    selectedId,
    loading,
    saving,
    error,
    canSelect: computed(() => Boolean(selectedId.value)),
    load,
    save,
    select,
  };
}
